import React, { createContext, useState, useContext, useEffect } from 'react';
import { API } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Initialize token from localStorage on mount
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      API.setToken(savedToken);
    }
    return savedToken;
  });
  const [loading, setLoading] = useState(true);

  // Set token in API client when it changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      API.setToken(token);
    } else {
      localStorage.removeItem('token');
      API.setToken(null);
    }
  }, [token]);

  // Load user on mount and when token changes
  useEffect(() => {
    const loadUser = async () => {
      if (token && !user) {
        try {
          // Ensure token is set in API client before making request
          API.setToken(token);
          const userData = await API.getMe();
          setUser(userData);
        } catch (error) {
          // Token invalid, clear it
          console.error('Failed to load user:', error);
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
        }
      } else if (!token) {
        // No token, ensure user is cleared
        setUser(null);
      }
      
      setLoading(false);
    };

    loadUser();
  }, [token]); // Run when token changes

  const login = async (username, password) => {
    try {
      const response = await API.login(username, password);
      // Set token immediately in API client before setting state
      API.setToken(response.token);
      setToken(response.token);
      setUser(response.user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await API.register(username, email, password);
      setToken(response.token);
      setUser(response.user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
